'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, FileCheck, Clock, Award } from 'lucide-react';

const features = [
{ icon: Shield, text: 'Assinaturas com validade jurídica no Brasil' },
{ icon: FileCheck, text: 'Conformidade com MP 2.200-2 e LGPD' },
{ icon: Clock, text: 'Fluxo sequencial ou simultâneo configurável' },
{ icon: Award, text: 'Suporte à ICP-Brasil, Assinatura avançada e básica.' }];


export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div suppressHydrationWarning className="min-h-screen flex bg-[#050d1a]">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] bg-gradient-to-br from-[#0a1628] via-[#071020] to-[#050d1a] flex-col justify-between p-10 relative overflow-hidden shrink-0 border-r border-white/10">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-[#1a56db] -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#1a56db] translate-x-1/3 translate-y-1/3" />
          <div className="absolute top-1/2 right-0 w-48 h-48 rounded-full bg-[#0d9488] translate-x-1/2" />
        </div>
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-xl flex items-center justify-center">
              <AppLogo size={28} />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">RD2Sign</span>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-full text-xs font-semibold text-[#60a5fa] mb-4">
              <span className="w-1.5 h-1.5 bg-[#60a5fa] rounded-full animate-pulse" />
              Plataforma de Assinatura Digital
            </div>
            <h1 className="text-white text-3xl font-bold leading-tight mb-3">
              Assinaturas digitais<br />com validade jurídica
            </h1>
            <p className="text-white/50 text-[15px] leading-relaxed">
              Envie, gerencie e colete assinaturas em documentos PDF com segurança e conformidade legal no Brasil.
            </p>
          </div>

          <div className="space-y-3.5">
            {features?.map((f, i) =>
            <div key={`feature-${i}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#1a56db]/20 border border-[#1a56db]/30 flex items-center justify-center shrink-0">
                  <f.icon size={16} className="text-[#60a5fa]" />
                </div>
                <span className="text-white/70 text-[13.5px] font-medium">{f?.text}</span>
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 hidden">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#0EA472] flex items-center justify-center text-white text-xs font-bold">MV</div>
              <div>
                <p className="text-white text-[12px] font-600">Marcos Vinícius Almeida</p>
                <p className="text-white/50 text-[11px]">Gerente Jurídico — TechBrasil</p>
              </div>
            </div>
            <p className="text-white/60 text-[12.5px] italic leading-relaxed">
              "Reduzimos o tempo de fechamento de contratos de 2 semanas para menos de 48 horas."
            </p>
          </div>
          <p className="text-white/30 text-[11px] mt-4 text-center">© 2026 RD2Buzz. Todos os direitos reservados.</p>
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#050d1a]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-white">RD2Sign</span>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-white/5 border border-white/10 rounded-lg p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-[13.5px] font-600 rounded-md transition-all duration-200 ${
              mode === 'login' ? 'bg-[#1a56db] text-white shadow-lg shadow-[#1a56db]/30' : 'text-white/50 hover:text-white'}`
              }>
              Entrar
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 text-[13.5px] font-600 rounded-md transition-all duration-200 ${
              mode === 'signup' ? 'bg-[#1a56db] text-white shadow-lg shadow-[#1a56db]/30' : 'text-white/50 hover:text-white'}`
              }>
              Assinar Plano
            </button>
          </div>

          {mode === 'login' ?
          <LoginForm onSwitchToSignup={() => setMode('signup')} /> :
          <SignupForm onSwitchToLogin={() => setMode('login')} />
          }
        </div>
      </div>
    </div>
  );

}